import { useEffect, useRef, useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import {
  MAX_CHECKLIST_PROMPTS,
  MAX_CHECKLIST_PROMPT_CHARS,
  templateChecklistDefault,
  type ReviewChecklistMode,
  type ReviewChecklistSetting,
} from '../../shared/reviewChecklist';
import type { TemplateKey } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';
import ReviewChecklistCard from './ReviewChecklistCard';

interface Props {
  scriptId: string;
  templateKey: TemplateKey;
  setting: ReviewChecklistSetting;
  /** Called after a successful save so the parent can refresh the loaded
   *  script without refetching. */
  onSaved: (next: ReviewChecklistSetting) => void;
}

// "Testimonial defaults" in the hint; the template's own word for itself.
const TEMPLATE_NAMES: Record<TemplateKey, string> = {
  testimonial: 'Testimonial',
  explainer: 'Explainer',
  brand_film: 'Brand film',
  social_short: 'Social / short-form',
  fb_ad_campaign: 'Facebook Ad Campaign',
  interview_brief: 'Interview brief',
};

const MODES: Array<{ mode: ReviewChecklistMode; label: string }> = [
  { mode: 'template', label: 'Template' },
  { mode: 'custom', label: 'Custom' },
  { mode: 'off', label: 'Off' },
];

const LINK_BTN =
  'font-mono text-[10px] uppercase tracking-[0.12em] text-muted hover:text-fg transition-colors ' +
  'focus:outline-none focus-visible:shadow-focus rounded-sm';

interface Row {
  key: number;
  text: string;
}

/**
 * C8 — per-script "What we need from you" control in the Admin settings
 * group: template defaults, custom prompts or off. Template and Off save on
 * click; Custom opens an editor with Save and Cancel. Custom prompts are kept
 * while on Template or Off, so switching back brings them back.
 */
export default function ReviewChecklistPanel({ scriptId, templateKey, setting, onSaved }: Props) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const nextKey = useRef(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const focusIndex = useRef<number | null>(null);

  const defaults = templateChecklistDefault(templateKey);
  const templateName = TEMPLATE_NAMES[templateKey] ?? 'Template';
  const shownMode: ReviewChecklistMode = editing ? 'custom' : setting.mode;

  // Restore focus after a reorder or added row.
  useEffect(() => {
    if (focusIndex.current === null) return;
    inputRefs.current[focusIndex.current]?.focus();
    focusIndex.current = null;
  });

  function toRows(prompts: string[]): Row[] {
    return prompts.map((text) => ({ key: nextKey.current++, text }));
  }

  async function save(next: { mode: ReviewChecklistMode; prompts?: string[] }) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await adminApi.setScriptReviewChecklist(scriptId, next);
      onSaved(res.review_checklist);
      setEditing(false);
      if (next.mode === 'custom' && res.review_checklist.mode === 'off') {
        setNotice('No prompts left, so the checklist is now off for this script.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function startCustom() {
    // Starts from the stored custom prompts, else the template defaults, so
    // Henry only changes what's different.
    const start = setting.prompts.length > 0 ? setting.prompts : defaults.prompts;
    setRows(toRows(start.length > 0 ? start : ['']));
    setError(null);
    setNotice(null);
    setEditing(true);
  }

  function pick(mode: ReviewChecklistMode) {
    if (saving) return;
    if (mode === 'custom') {
      if (!editing) startCustom();
      return;
    }
    if (mode === setting.mode && !editing) return;
    void save({ mode });
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= rows.length || from === to) return;
    setRows((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    focusIndex.current = to;
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  const filled = rows.filter((r) => r.text.trim()).length;

  return (
    <section data-print-hide className="bg-card border border-line rounded-md px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>// Review checklist</Eyebrow>
        {(editing || setting.mode === 'custom' || (setting.mode === 'template' && defaults.enabled)) && (
          <button type="button" onClick={() => setPreviewOpen(true)} className={LINK_BTN}>
            Preview
          </button>
        )}
      </div>

      <div
        role="radiogroup"
        aria-label="Review checklist"
        className="grid grid-cols-3 gap-0.5 p-[3px] rounded-full bg-elevated border border-line"
      >
        {MODES.map(({ mode, label }) => {
          const on = shownMode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={saving}
              onClick={() => pick(mode)}
              className={`
                rounded-full px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-colors
                focus:outline-none focus-visible:shadow-focus disabled:opacity-60
                ${on
                  ? 'bg-card text-accent shadow-[0_1px_4px_-1px_rgba(11,15,13,0.18),inset_0_0_0_1px_rgba(20,86,75,0.25)]'
                  : 'text-muted hover:text-fg'}
              `}
            >
              {label}
            </button>
          );
        })}
      </div>

      {editing ? (
        <div>
          <ol className="mt-3 flex flex-col gap-1.5">
            {rows.map((row, i) => (
              <li
                key={row.key}
                className={`flex items-center gap-1.5 ${dragIndex === i ? 'opacity-50' : ''}`}
                onDragOver={(e) => {
                  if (dragIndex === null) return;
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, i);
                  setDragIndex(null);
                }}
              >
                <span
                  draggable
                  onDragStart={(e) => {
                    setDragIndex(i);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  title="Drag to reorder, or Alt + arrow keys"
                  aria-hidden
                  className="shrink-0 w-3.5 flex justify-center text-faint cursor-grab active:cursor-grabbing"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
                    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
                    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                  </svg>
                </span>
                <input
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  value={row.text}
                  maxLength={MAX_CHECKLIST_PROMPT_CHARS}
                  aria-label={`Prompt ${i + 1}`}
                  onChange={(e) =>
                    setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, text: e.target.value } : r)))
                  }
                  onKeyDown={(e) => {
                    if (e.altKey && e.key === 'ArrowUp') {
                      e.preventDefault();
                      move(i, i - 1);
                    } else if (e.altKey && e.key === 'ArrowDown') {
                      e.preventDefault();
                      move(i, i + 1);
                    } else if (e.key === 'Escape') {
                      cancelEdit();
                    }
                  }}
                  disabled={saving}
                  className="
                    flex-1 min-w-0 px-3 py-2 bg-card border border-line rounded-sm
                    text-fg text-[13px] placeholder:text-faint
                    focus:border-accent focus:shadow-focus focus:outline-none
                    disabled:opacity-50
                  "
                  placeholder="A short question for the reviewer"
                />
                <button
                  type="button"
                  aria-label={`Remove prompt ${i + 1}`}
                  onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  disabled={saving}
                  className="shrink-0 w-[26px] h-[26px] rounded-full inline-flex items-center justify-center text-faint hover:text-error hover:bg-error/10 focus:outline-none focus-visible:shadow-focus"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </li>
            ))}
          </ol>
          {rows.length < MAX_CHECKLIST_PROMPTS && (
            <button
              type="button"
              onClick={() => {
                setRows((prev) => [...prev, { key: nextKey.current++, text: '' }]);
                focusIndex.current = rows.length;
              }}
              disabled={saving}
              className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] font-semibold text-accent hover:text-accent-hover focus:outline-none focus-visible:shadow-focus rounded-sm"
            >
              + Add prompt
            </button>
          )}
          <div className="mt-3.5 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save({ mode: 'custom', prompts: rows.map((r) => r.text) })}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
            <span className="ml-auto text-[11px] text-faint">
              {filled} of {MAX_CHECKLIST_PROMPTS}
            </span>
          </div>
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Starts from the template defaults so you only change what's different.
          </p>
        </div>
      ) : setting.mode === 'off' ? (
        <>
          <p className="mt-3 text-[13px] text-muted">The client won't see a checklist on this {templateKey === 'interview_brief' ? 'brief' : 'script'}.</p>
          {setting.prompts.length > 0 && (
            <p className="mt-2 text-faint text-[11px] leading-relaxed">
              Any custom prompts are kept, so switching back to Custom brings them back.
            </p>
          )}
        </>
      ) : setting.mode === 'custom' ? (
        <>
          <PromptList prompts={setting.prompts} />
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Custom prompts for this script.{' '}
            <button type="button" onClick={startCustom} className="font-medium text-accent hover:text-accent-hover">
              Edit them
            </button>
          </p>
        </>
      ) : defaults.enabled ? (
        <>
          <PromptList prompts={defaults.prompts} />
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            {templateName} defaults. Choose Custom to change them for this script.
          </p>
        </>
      ) : (
        <>
          <p className="mt-3 text-[13px] text-muted">
            The client won't see a checklist: interview briefs start with it off.
          </p>
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Choose Custom to add prompts for this brief.
          </p>
        </>
      )}

      {notice && <p className="mt-2 text-[12px] text-muted">{notice}</p>}
      {error && <p className="mt-2 text-error text-[12px]">{error}</p>}

      {previewOpen && (
        <ChecklistPreview
          prompts={
            editing
              ? rows.map((r) => r.text.trim()).filter(Boolean)
              : setting.mode === 'custom'
                ? setting.prompts
                : defaults.prompts
          }
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </section>
  );
}

function PromptList({ prompts }: { prompts: string[] }) {
  return (
    <ol className="mt-3 flex flex-col gap-1.5">
      {prompts.map((prompt, i) => (
        <li key={`${i}-${prompt}`} className="flex items-center gap-2 text-[13px] leading-[1.4] text-muted">
          <span className="shrink-0 w-[18px] h-[18px] rounded-full bg-line-soft text-muted font-mono text-[9px] font-bold inline-flex items-center justify-center">
            {i + 1}
          </span>
          {prompt}
        </li>
      ))}
    </ol>
  );
}

/** The client card exactly as the reviewer sees it (unticked, not
 *  clickable), at a realistic width so Henry can check line lengths. */
function ChecklistPreview({ prompts, onClose }: { prompts: string[]; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink-900/40 p-4 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Review checklist preview"
        className="w-full max-w-[960px] bg-page border border-line rounded-md shadow-flare-lg p-4 md:p-6 animate-rise-subtle"
      >
        <div className="flex items-center justify-between mb-3">
          <Eyebrow>Preview · as the client sees it</Eyebrow>
          <button ref={closeRef} type="button" onClick={onClose} className={LINK_BTN}>
            Close
          </button>
        </div>
        {prompts.length > 0 ? (
          <ReviewChecklistCard prompts={prompts} slug="preview" status="preview" preview />
        ) : (
          <p className="text-[13px] text-muted">Add a prompt to see the card.</p>
        )}
      </div>
    </div>
  );
}
